import PhoneInputPrimitive, { Props as PhoneInputProps } from 'react-phone-number-input'
import 'react-phone-number-input/style.css'

import { cn } from '@/customer/lib/utils'

/**
 * Thin wrapper around `react-phone-number-input` styled to sit alongside
 * our shadcn `<Input />`. Emits E.164 strings via `onChange` (`undefined`
 * when the field is empty).
 *
 * The library ships class-named children we can't reach directly, so we
 * style them via Tailwind arbitrary variants on the wrapper `className`.
 * Under the hood the library uses the `min` metadata build - light on
 * bundle size, sufficient for possible-length validation. Full E.164
 * validation happens server-side.
 *
 * The `error` prop mirrors the `aria-invalid` styling on our other
 * inputs. It's an explicit prop rather than piggybacking on `aria-invalid`
 * because the library doesn't reliably forward that attribute to the root
 * element (it goes to the underlying `input` instead).
 */
export function PhoneInput({
  className,
  onChange,
  error,
  ...props
}: PhoneInputProps<React.ComponentPropsWithoutRef<"input">> & { error?: boolean }) {
  return (
    <PhoneInputPrimitive
      international
      defaultCountry="GB"
      onChange={onChange}
      {...props}
      className={cn(
        // Wrapper matches the shadcn Input shell.
        'flex h-12 w-full min-w-0 items-center gap-2 rounded-lg border-2 border-foreground bg-background px-4 text-body-md transition-colors',
        'focus-within:border-ring focus-within:ring-2 focus-within:ring-ring/30',
        // Error state - matches `aria-invalid` styling on shadcn Input.
        // The focus-within variants ensure the destructive colour wins over
        // the default focus ring when the field is both invalid and focused.
        error &&
          'border-destructive ring-2 ring-destructive/30 focus-within:border-destructive focus-within:ring-destructive/30',
        // Nested library elements.
        '[&_.PhoneInputInput]:h-full [&_.PhoneInputInput]:flex-1 [&_.PhoneInputInput]:border-0 [&_.PhoneInputInput]:bg-transparent [&_.PhoneInputInput]:text-body-md [&_.PhoneInputInput]:text-foreground [&_.PhoneInputInput]:outline-none [&_.PhoneInputInput]:placeholder:text-muted-foreground',
        '[&_.PhoneInputCountry]:flex [&_.PhoneInputCountry]:items-center [&_.PhoneInputCountry]:gap-1',
        '[&_.PhoneInputCountryIcon--border]:bg-transparent [&_.PhoneInputCountryIcon--border]:shadow-none',
        className
      )}
    />
  )
}
